import * as Sentry from '@sentry/vue'
import { omit } from 'lodash-es'
import { onCLS, onLCP, onFCP, onTTFB, onINP, type Metric } from 'web-vitals'
import { sensorsRegisterPage, TRACK_EVENT } from '~/utils/track'
import {
  COOKIE_KEY,
  INVITE_CODE,
  SESSION_REF,
  SESSION_SOURCE,
} from '~/constants/config'

export default defineNuxtPlugin(async ({ vueApp }) => {
  const runtimeConfig = useRuntimeConfig()
  const route = useRoute()
  const session = useSession()

  // const serverRequest401 = useServerRequest401()

  const { privatization, area } = runtimeConfig.public

  if (import.meta.client) {
    // 其他来源注册
    if (route?.query?.from) {
      sessionSetItem(SESSION_SOURCE, route.query.from)
    }

    if (route.query.ref || route.query.via) {
      sessionSetItem(SESSION_REF, route.query.ref || route.query.via)
    }

    // 拉新邀请码
    if (route?.query?.code) {
      sessionSetItem(INVITE_CODE, route.query.code)
    }
  }

  // const permissionData = usePermissionData()

  // if (serverRequest401.value) {
  //   deleteCookie(COOKIE_KEY.USER_TOKEN)
  //   useLoginOut()
  //   message.destroy()
  //   serverRequest401.value = false
  //   if (permissionData.value?.config?.logout_url && import.meta.client) {
  //     location.href = permissionData.value?.config?.logout_url
  //   }
  // }

  if (privatization) {
    return {}
  }

  if (area === 'US') {
    useHead({
      link: [{ rel: 'preconnect', href: 'https://www.googletagmanager.com' }],
      script: [
        {
          // google analytics
          innerHTML:
            "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-TWNCS4ZG');",
        },
      ],
      noscript: [
        {
          // google analytics
          textContent:
            '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TWNCS4ZG" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
        },
      ],
    })
  }

  let sa: any

  /** 埋点放到最前面，因为部分接口调用需要触发埋点逻辑 */
  if (process.env.NODE_ENV === 'production' && import.meta.client) {
    // if (import.meta.client) {
    const sensorsInit = (sensors: any) => {
      const project =
        runtimeConfig.public.env === 'production' ? 'production' : 'default'
      sensors.use('PageLeave', {})

      sensors.init({
        server_url: `https://mofakeji.datasink.sensorsdata.cn/sa?project=${project}&token=eb771e11f364630b`,
        is_track_single_page: true, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
        use_client_time: true,
        send_type: 'beacon',
        show_log: project === 'default',
        heatmap: {
          // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭
          clickmap: 'default',
          // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件 可以设置 'default' 表示开启
          scroll_notice_map: 'default',
        },
      })

      if (session.value?.user_id) {
        sensorsLogin(session.value.user_id)
      }

      sensorsRegisterPage()

      sensors.quick('autoTrack')
      ;(window.$sensors as any) = sensors
    }

    if (area === 'CN') {
      /** 易分析埋点 */
      useHead({
        script: [
          {
            innerHTML: `var _yfx_trackdata = _yfx_trackdata || [];
              var _yfx_website = "10018517";
              var _yfx_netrandom = 100;
              var _yfx_domrandom = 100;
              var _yfx_videoplayerid ="resourceVideo";
              (function() {
                  var yfxjs = document.createElement("script");
                  yfxjs.charset = "utf-8";
                  yfxjs.src = "//cdn.yeefx.cn/yeefxcount.js";
                  var yfxjs_t = document.getElementsByTagName("script")[0];
                  yfxjs_t.parentNode.insertBefore(yfxjs, yfxjs_t);
              })();`,
          },
        ],
      })
    }

    Sentry.init({
      dsn: 'https://02854a0ae8ea493189e0ddde300e7b0e@xmov-frontend-sentry.xmov.ai/6',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      release: `${runtimeConfig.public.env}:${runtimeConfig.public.release}`,
      environment: `${runtimeConfig.public.env}:${runtimeConfig.public.area}`,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.+youyan30/,
        '192.168',
      ],
      tracesSampleRate: 1.0,
    })

    sa = await import('sa-sdk-javascript')

    sensorsInit(sa)

    // 初始化性能指标
    function sendToAnalytics(metric: Metric, evt: TRACK_EVENT) {
      sa.track(evt, omit(metric, 'entries', 'id'))
    }

    onCLS((metric) => sendToAnalytics(metric, TRACK_EVENT.VITALS_CLS))
    onLCP((metric) => sendToAnalytics(metric, TRACK_EVENT.VITALS_LCP))
    onFCP((metric) => sendToAnalytics(metric, TRACK_EVENT.VITALS_FCP))
    onTTFB((metric) => sendToAnalytics(metric, TRACK_EVENT.VITALS_TTFB))
    onINP((metric) => sendToAnalytics(metric, TRACK_EVENT.VITALS_INP))
  }

  return {
    provide: {
      sentry: Sentry,
      sensors: sa,
    },
  }
})
